// @ts-strict-ignore
import {
  forwardRef,
  Inject,
  Injectable,
  NgZone,
  ViewContainerRef,
  Injector
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Observable, throwError } from 'rxjs';
import { SohoToastService } from 'ids-enterprise-ng';
import { unescape } from 'lodash-es';
import { escape } from 'lodash-es';
import { isEmpty } from 'lodash-es';

import * as urltemplate from 'url-template';

import { AppConstants } from './app.constants';
import { LMGlobalPersonalization } from '../states/models';
import { LMGlobalPersonalizationService } from '../states/services';
import { LMGlobalUtilService } from './lm-global-util.service';
import { LMActionFormDialogService, LMActionFormDialogResult, LMActionFormDialogResultCode } from '../components/modal/lm-action-form-dialog.service';
import {
  LMBusyIndicatorService,
  LMContextService,
  LMErrorUtilService,
  LMLoggerService,
  LMSpecService,
  LMUrlInfoBuilderService,
  LMUrlUtilService
} from '../services';
import { LMNavigationService } from '@app/components';
import { ActionResource } from '@app/resources';
import { NavigationExtras } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { first } from 'rxjs/operators';

@Injectable()
export class LMContextUtilService {

  private isEnabled = false;
  private sentAppInitComplete = false;
  private logicalId: string;
  private webpartScreenId: string;
  private title: string;
  private lastBusinessContextMessage: any;
  private dialogPlaceholder: ViewContainerRef;
  private lmActionFormDialogService: LMActionFormDialogService;
  private startModeCompleted = false;

  private bodReferenceFields = {
    DocumentID: { required: true, bodName: 'documentId', setName: 'DocumentID' },
    AccountingEntity: { required: false, bodName: 'accountingEntity', setName: 'AccountingEntity' },
    Location: { required: false, bodName: 'location', setName: 'Location' },
    Revision: { required: false, bodName: 'revisionId', setName: 'Revision' }
  };

  constructor(
    protected lmContextService: LMContextService,
    private lmLoggerService: LMLoggerService,
    private lmUrlUtilService: LMUrlUtilService,
    private lmGlobalUtilService: LMGlobalUtilService,
    private lmSpecService: LMSpecService,
    private titleService: Title,
    private sohoToastService: SohoToastService,
    @Inject(forwardRef(() => LMErrorUtilService)) private lmErrorUtilService, // LMErrorUtilService,
    private lmGlobalPersonalizationService: LMGlobalPersonalizationService,
    private lmUrlInfoBuilderService: LMUrlInfoBuilderService,
    private lmBusyIndicatorService: LMBusyIndicatorService,
    private injector: Injector,
    private ngZone: NgZone,
    private http: HttpClient,
  ) {

    this.webpartScreenId = this.lmContextService.getWebpartScreenId();
    this.logicalId = this.lmContextService.getLogicalId();

    this.isEnabled = (this.logicalId !== null && this.logicalId.length > 0);
    if (this.isEnabled) {
      this.webpartScreenId = (this.webpartScreenId) ? this.webpartScreenId : this.logicalId.split('.')[ 1 ];
      this.logicalId = unescape(this.logicalId);
      this.lmLoggerService.log([ 'LMContextUtilService', 'registeredMessageHandlers' ]);
      this.registerMessageHandlers();

      if (this.lmContextService.useMingleStartMode()) {
        const startModeParamValue = this.lmUrlUtilService.getUrlParam(window.location.href, 'inforStartMode');
        this.lmLoggerService.log([ 'LMContextUtilService', 'inforStartMode', startModeParamValue ]);
        this.startModeCompleted = (startModeParamValue && startModeParamValue > 0) ? false : true;
      } else {
        this.startModeCompleted = true;
      }
    }
  }

  public registerMessageHandlers() {
    // remove any existing favorite handler
    infor.companyon.client.unRegisterMessageHandler('showFavoriteContext');

    // register a bookmark (favorite or shortcut) handler
    infor.companyon.client.registerMessageHandler('showFavoriteContext', (data: any) => {
      this.lmLoggerService.log([ 'LMContextUtilService', 'showFavoriteContext', data ]);
      this.startModeCompleted = true;
      this.processFavoriteContext(data);
    });

    // remove any existing drillback handler
    infor.companyon.client.unRegisterMessageHandler('applicationDrillback');

    // register a drillback handler
    infor.companyon.client.registerMessageHandler('applicationDrillback', (data: any) => {
      this.lmLoggerService.log([ 'LMContextUtilService', 'applicationDrillback', data ]);
      this.startModeCompleted = true;

      const drillbackIdentifier = 'drillbackURL=';
      const index = data.applicationDrillback.indexOf(drillbackIdentifier);
      let url = data.applicationDrillback.substring(index + drillbackIdentifier.length);

      // check for non encoded ampersand. Anything after that is not our url but meant for mingle/portal usage.
      const ampersandIndex = url.indexOf('&');
      if (ampersandIndex > -1) {
        url = url.substring(0, ampersandIndex);
      }

      if (this.lmContextService.inMingleWidget()) {
        // Widgets should not respond to drillback messages. They do still
        // need to register message handlers because of inforStartMode.
        this.lmLoggerService.log([ 'LMContextUtilService', 'registerMessageHandler', 'widget does not respond to drillback messages' ]);
        return;
      }

      // todo: would really like to be able to rely on the drillbackUrl being fully encoded
      //  but with portal drillbacks I can't guarantee it. This causes me not to be able to
      //  use a hash for a selected panel of a composite form or page.
      // // get url param w/o decoding it.
      // const url = this.lmUrlUtilService.getUrlParam(data.applicationDrillback, 'drillbackURL');

      // is url a BI Drillback - LandmarkBIDrillback URL param from Infor Application View > ViewId "LandmarkBIDrillback"
      if (data.applicationDrillback.indexOf('LandmarkBIDrillback') > -1) {
        this.processLandmarkBIDrillback(data);
        return;
      }

      if (data.applicationDrillback.indexOf('ProcessLinkDrillback') > -1) {
        this.processLinkDrillbackRequest(decodeURIComponent(url));
        return;
      }

      if (data.applicationDrillback.indexOf('RequestActionDrillback') > -1) {
        this.actionDrillbackRequest(decodeURIComponent(url));
        return;
      }

      if (data.applicationDrillback.indexOf('InboxNotificationDrillback') > -1) {
        this.processInboxNotification(data);
        return;
      }

      if (data.applicationDrillback.indexOf('InboxWorkflowDrillback') > -1) {
        this.processInboxWorkflow(data);
        return;
      }

      // if (index === -1 || url === null || url === '') {
      if (!url) {
        this.lmLoggerService.log([ 'LMContextUtilService', 'registerMessageHandler', 'drillbackURL was empty or not provided' ]);
        return;
      }

      if (data.applicationDrillback.indexOf('drillbackURL=') === -1) {
        // if no drillback is passed navigate to web/home.
        this.processSuiteHome();
        return;
      }

      this.lmContextService.setClearNextPageState(true);
      this.processStandardDrillbackRequest(decodeURIComponent(url));
    });
  }

  public hasStartModeCompleted(): boolean {
    return this.startModeCompleted;
  }

  public clearAll() {
    // console.log('clearAll');
    if (!this.isEnabled) {
      return;
    }

    this.lmLoggerService.log([ 'LMContextUtilService', 'clearAll' ]);
    infor.companyon.client.sendMessage('clearAllContext', {});
  }

  public disableShortcut() {
    if (!this.isEnabled) {
      return;
    }

    const data = {
      name: '',
      description: '',
      absoluteURL: 'false',
      shortcutContext: ''
    };

    this.publishShortcut(data);
  }

  public enableShortcut(params: any, performClearAll = true) {
    // console.log('enableShortcut performClearAll = ' + performClearAll);
    this.lmLoggerService.log([ 'LMContextUtilService', 'enableShortcut', params ]);

    // params: name, description, shortcutContext, absoluteURL
    if (!this.isEnabled) {
      return;
    }

    if (performClearAll) {
      this.clearAll();
    }

    if (params.absoluteURL === undefined || params.absoluteURL === null) {
      params.absoluteURL = 'false';
    }

    this.publishShortcut(params);
  }

  private createStandardShortcutParams(title = ''): any {
    this.title = title ? title : this.titleService.getTitle();
    const logicalIdParams = this.lmUrlUtilService.getPrefixedParams(window.location.href, 'LogicalId');

    let shortcutUrl = '';
    if (isEmpty(logicalIdParams)) {
      // ----------------------------------------------------------
      // This code will ensure that adding a LogicalId to the url
      // params won't cause it to be placed after any #hash on
      // the url. For example if there is a page panel using a
      // hash to navigate directly to a tab in the page.
      // ----------------------------------------------------------
      const url = new URL(window.location.href);
      url.searchParams.set('LogicalId', this.logicalId);
      shortcutUrl = url.href;
    } else {
      shortcutUrl = window.location.href;
    }

    const params = {
      name: this.title,
      description: '',
      absoluteURL: 'false',
      shortcutContext: shortcutUrl
    };

    return params;
  }

  public enableStandardShortcut(performClearAll = true, title = '') {
    if (!this.isEnabled) {
      return;
    }

    if (!this.sentAppInitComplete) {
      this.sentAppInitComplete = true;
      this.lmLoggerService.log([ 'LMContextUtilService', 'appInitComplete' ]);
      infor.companyon.client.sendMessage('appInitComplete', window.name);
    }

    this.lmLoggerService.time({ id: 'LMContextUtilService.enableStandardShortcut()' });

    const params = this.createStandardShortcutParams(title);
    this.enableShortcut(params, performClearAll);
    this.lmLoggerService.timeEnd('LMContextUtilService.enableStandardShortcut()');

  }

  public sendDrillbackMessage(url: string) {
    if (this.isEnabled && (this.lmContextService.inMingleWidget() || this.lmContextService.drillAroundWindow())) {
      const drillbackUrl = '?LogicalId=' + escape(this.logicalId) + '&drillbackURL=' + encodeURIComponent(url);
      this.publishDrillbackMessage(drillbackUrl);
    }
  }

  private getCustomHTML() {
    return '<p style="color:red;">Use this to clear the selected Index textbox</p>tt<a href="http://www.google.com">Google</a>tt';
  }

  public getLocation(href) {
    const l = document.createElement('a');
    l.href = href;
    return l;
  }

  public sendContextMessages(params: any) {
    // params: screenId, messageText, entities, standard
    if (!this.isEnabled || this.lmContextService.drillAroundWindow()) {
      return;
    }

    setTimeout(() => {
      this.lmLoggerService.time({ id: 'LMContextUtilService.sendContextMessages()' });

      for (const name in params.standard) {
        if (params.standard.hasOwnProperty(name)) {
          this.publishStandardContext(name, params.standard[ name ]);
        }
      }

      if (params.entities.length > 0) {
        const changeNoun = params.entities[0].bodReference ? true : false;
        const noun = changeNoun ? params.entities[0].bodReference.noun : '';
        if (!params.noDrillbackUrl) {
          for (let i = 0, entityLen = params.entities.length; i < entityLen; i++) {
            const entity = params.entities[ i ];
            entity.drillbackURL = '?LogicalId=' + escape(this.logicalId) + '&drillbackURL=' + encodeURIComponent(window.location.href);
          }
        }

        const data: any = {
          screenId: this.webpartScreenId + '_' + params.screenId,
          logicalId: this.logicalId,
          originatingTime: new Date().getTime(),
          entities: params.entities,
        };

        if (params.messageText.length > 0) {
          data.messageText = params.messageText;
        }

        if (!params.doNotPersistMessage) {
          this.lastBusinessContextMessage = data;
        }
        this.publishBusinessContext(data);
      }

      this.lmLoggerService.timeEnd('LMContextUtilService.sendContextMessages()');
    });
  }

  public sendMessagesForConfigHome(params: any) {
    // params: dataArea, businessClass, title
    const dataArea: string = params.dataArea || 'home';
    const businessClass: string = params.businessClass || '';
    const title: string = params.title || '';
    const resourceType = AppConstants.RESOURCETYPE_HOME;

    this.enableStandardShortcut(true, title);

    const entities = [ {
      entityType: dataArea,
      id1: this.title
    } ];

    this.sendContextMessages({
      screenId: resourceType + '_' + businessClass,
      messageText: Soho.Locale.translate('Sharing') + ' ' + this.title + ' ',
      entities,
    });
  }

  public sendMessagesForLandingPage(params: any) {
    // params: dataArea, landingPage, title
    const dataArea: string = params.dataArea || 'land';
    const landingPage: string = params.landingPage || '';
    const title: string = params.title || '';
    const resourceType = AppConstants.RESOURCETYPE_LAND;

    this.enableStandardShortcut(true, title);

    const entities = [ {
      entityType: dataArea,
      id1: this.title
    } ];

    this.sendContextMessages({
      screenId: resourceType + '_' + landingPage,
      messageText: Soho.Locale.translate('Sharing') + ' ' + this.title + ' ',
      entities,
    });
  }

  public sendMessagesForHtmlPage(params: any) {
    // params: dataArea, webApp, title
    if (!this.isEnabled) {
      return;
    }

    const dataArea: string = params.dataArea || 'html';
    const webApp: string = params.webApp || '';
    const title: string = params.title || '';
    const resourceType = AppConstants.RESOURCETYPE_HTML;

    this.enableStandardShortcut(true, title);

    const entities = [ {
      entityType: dataArea,
      id1: this.title
    } ];

    this.sendContextMessages({
      screenId: resourceType + '_' + webApp + '_' + this.title,
      messageText: Soho.Locale.translate('Sharing') + ' ' + this.title + ' ',
      entities,
    });
  }

  public mergeContextMessages(params: any) {
    // params: sourceMsgs, newMsgs
    const sourceMsgs = params.sourceMsgs;
    const addMsgs = params.addMsgs;

    if (!sourceMsgs.entities || !addMsgs || !addMsgs.entities) {
      return sourceMsgs;
    }

    sourceMsgs.entities = sourceMsgs.entities.concat(addMsgs.entities);
    return sourceMsgs;
  }

  public setDrillbackDialogElement(dialogElementRef: ViewContainerRef, lmActionFormDialogService: LMActionFormDialogService) {
    this.dialogPlaceholder = dialogElementRef;
    this.lmActionFormDialogService = lmActionFormDialogService;
  }

  public buildContextMessages(params: any): Observable<any> {
    const busFormName: string = params.busFormName || '';
    const busClassName: string = params.busClassName || '';
    const widgetEventPrefix: string = params.widgetEventPrefix;
    const dataObj: any = params.dataObj;
    const rows: any = params.rows || null;
    const activeRow: any = (params.activeRow !== undefined) ? params.activeRow : null;
    const busContextMessages: any = params.busContextMessages || null;
    const busContextInvocations: any = params.busContextInvocations || null;
    const bodReferenceFields = this.bodReferenceFields;

    let noun = '';
    const aryStandard: any = {};
    let aryEntities: Array<any> = [];
    return new Observable(observer => {

      if (!this.isEnabled) {
        observer.next({ status: AppConstants.STATUS_CANCEL });
        observer.complete();
        return;
      }

      setTimeout(() => {
        this.lmLoggerService.time({ id: 'LMContextUtilService.buildContextMessages()' });

        if (busContextInvocations && busContextMessages) {
          for (let i = 0, invocationLen = busContextInvocations.length; i < invocationLen; i++) {
            const invocation: any = busContextInvocations[ i ];
            const message: any = busContextMessages[ invocation.name ];

            if (!message) {
              continue;
            }

            const data: any = buildContextMessage(message);

            if (message.businessEntity === 'inforBusinessContext') {
              aryEntities = aryEntities.concat(data.entity);
            } else {
              aryStandard[ message.businessEntity ] = data;
            }
          }
        }

        // An empty list sends the default business context message
        if (widgetEventPrefix === AppConstants.RESOURCETYPE_LIST && dataObj && (dataObj.data === undefined || dataObj.data.length === 0)) {
          aryEntities = [];
        }

        if (aryEntities.length === 0) {
          const entity: any = {
            entityType: this.lmContextService.getUrlPdlOrDa(),
            id1: this.titleService.getTitle()
          };
          aryEntities.push(entity);
        }

        addBodReferenceToEntities();

        const retObj = {
          entities: aryEntities,
          standard: aryStandard,
          messageText: Soho.Locale.translate('Sharing') + ' ' + this.titleService.getTitle() + ' ',
          screenId: widgetEventPrefix + '_' + busClassName + (busFormName.length > 0 ? '_' + busFormName : ''),
          status: AppConstants.STATUS_COMPLETED
        };

        observer.next(retObj);
        observer.complete();

        this.lmLoggerService.timeEnd('LMContextUtilService.buildContextMessages()');
      });
    });

    // --------------------------------------------------
    // private methods
    // --------------------------------------------------
    function buildContextMessage(message: any) {
      const data: any = {};

      for (const key in message) {
        if (key === 'businessEntity') {
          continue;
        }

        const val = message[ key ];
        if (Array.isArray(val)) {
          if (key === 'properties') {
            const properties = processProperties(val, activeRow);
            Object.assign(data, properties);
          } else {
            const group = processGroupProperties(val, key, message.businessEntity);
            data[ key ] = group;
          }
        } else {
          data[ key ] = val;
        }
      }

      return data;
    }

    function processGroupProperties(group: any, groupName: string, businessEntity: string) {
      const aryGroup = [];
      const useSelectedRows = processMessageForSelectedRows(groupName, businessEntity);

      if (useSelectedRows) {
        const item = group[ 0 ];
        for (let i = 0, rowsLen = rows.length; i < rowsLen; i++) {
          aryGroup.push(processProperties(item, rows[ i ]));
        }
      } else {
        for (let i = 0, groupLen = group.length; i < groupLen; i++) {
          const item = group[ i ];
          aryGroup.push(processProperties(item, activeRow));
        }
      }

      return aryGroup;
    }

    function processProperties(properties: any, row: any) {
      const data: any = {};

      for (let i = 0, propertyLen = properties.length; i < propertyLen; i++) {
        const item: any = properties[ i ];
        let value = '';

        if (item.type === 'text') {
          value = item.value;
        } else if (item.type === 'field') {
          value = getFieldValue(item.fieldId, row);
        }

        if (item.property === 'bod reference.noun') {
          noun = value;
        } else {
          data[ item.property ] = value;
        }
      }

      return data;
    }

    function processMessageForSelectedRows(groupName: string, businessEntity: string) {
      if (!rows || rows.length === 1) {
        return false;
      }

      return ((businessEntity === 'multipleAddress' && groupName === 'Addresses')
              || (businessEntity === 'packageInformation' && groupName === 'TrackingNumbers'));
    }

    function getField(fieldId: string, r: any): LMField {
      const row: any = (r !== undefined) ? r : null;
      let field: any = null;

      if (dataObj && dataObj.fields) {
        if (dataObj.fields.hasOwnProperty(fieldId)) {
          field = dataObj.fields[ fieldId ];
        }
      } else if (dataObj && dataObj.data && row != null && dataObj.data.length > row) {
        if (dataObj.data[ row ].fields.hasOwnProperty(fieldId)) {
          field = dataObj.data[ row ].fields[ fieldId ];
        }
      }

      return field;
    }

    function getFieldValue(fieldId: string, r: any) {
      const row = (r !== undefined) ? r : null;
      let value = '';

      if (dataObj && dataObj.fields) {
        if (dataObj?.fields?.hasOwnProperty(fieldId)) {
          value = dataObj.fields[ fieldId ].value;
        }
      } else if (dataObj && dataObj.data && row !== null && dataObj.data.length > row) {
        if (dataObj.data[ row ]?.fields?.hasOwnProperty(fieldId)) {
          value = dataObj.data[ row ].fields[ fieldId ].value;
        }
      }

      return value;
    }

    function addBodReferenceToEntities() {
      const bodFieldId = busClassName + '_BODId';
      const bodId = getFieldValue(bodFieldId, activeRow);
      if (bodId.length === 0) {
        return;
      }

      const bodReference: any = {};
      const bodField: LMField = getField(bodFieldId, activeRow);

      bodField.members.map(memberId => ({
        reference: memberId.substring(memberId.lastIndexOf('_') + 1),
        memberValue: getFieldValue(memberId, activeRow)
      }))
        .filter(referenceObject => bodReferenceFields[ referenceObject.reference ])
        .forEach(referenceObject => {
          const bodName: any = bodReferenceFields[ referenceObject.reference ].bodName;
          bodReference[ bodName ] = referenceObject.memberValue;
        });

      if (noun.length === 0) {
        if (aryEntities.length > 0) {
          if (aryEntities[ 0 ].hasOwnProperty('entityType')) {
            const entityType = aryEntities[ 0 ].entityType;
            noun = entityType.replace(/^Infor/, '');
          }
        }

        if (noun.length === 0) {
          noun = busFormName;
        }
      }
      bodReference[ 'noun' ] = noun;

      for (let i = 0, entityLen = aryEntities.length; i < entityLen; i++) {
        aryEntities[ i ].bodReference = bodReference;
      }
    }
  }

  private processFavoriteContext(data: any): void {
    this.lmLoggerService.log([ 'LMContextUtilService', 'showFavoriteContext', data ]);

    if (data === null) {
      return;
    }
    let url = data.favoriteContext;
    if (url.indexOf('http%3A%2F%2F') === 0 || url.indexOf('https%3A%2F%2F') === 0) {
      // The url appears to be encoded. Apparently Mingle 11 encodes url using encodeURIComponent before sending the message.
      // But mingle 12 does not encode the URL.(LMCLIENT-33716)
      // So decode only when it is required to avoid errors like LMCLIENT-28847. In this ticket, the key field has '&'
      // and decoding the whole url will decode the key fields too.
      // lodash unescape was mistakenly used here. It does not work like regular unescape or decodeURIComponent (Remove lodash usage LMCLIENT-33757)
      // 3.5 uses regular unescape instead of decodeURIComponent
      url = decodeURIComponent(url);
    }
    this.navigateWebAppUrl(url);
  }

  private processInboxNotification(data: any): void {
    // Expect Drill back to contain: ?prodline={ID1}&notificationID={ID2}&InboxNotificationDrillback=true&LogicalId={LogicalId}
    // '?prodline=hcm&notificationID=c7e5aace-e180-8a00-0000-002064354439&InboxNotificationDrillback=true&LogicalId=lid%3A%2F%2Finfor.lawson-ghr.ghr110-ade-useast'
    let drillback = data.applicationDrillback as string;
    if (!drillback || drillback.length === 0) {
      this.lmLoggerService.log([ 'LMContextUtilService', 'processInboxNotification received no drillback data.' ]);
      return;
    }
    drillback = decodeURIComponent(drillback);
    if (drillback.startsWith('?')) {
      drillback = drillback.substring(1);
    }
    const params = {} as { InboxNotificationDrillback?: boolean, notificationID?: string, prodline?: string, LogicalId?: string };
    drillback.split('&').forEach(param => {
      const nameValue = param.split('=');
      params[nameValue[0]] = nameValue[1];
    });

    if (!params?.notificationID) {
      this.lmLoggerService.log([ 'LMContextUtilService', 'processInboxNotification', 'Notification ID is not available in drillback data', data ]);
      return;
    }
    // Assumption: Mingle or portal will go to the homepage first before posted message is handled in registered handlers.
    // If so, the context is created and the UrlPdlOrDa and logicalId are set in context.
    const actionResource = new ActionResource({ busClassName: 'UserNotification', keyValueArray: [ params.notificationID ], actionName: '_open' });
    const url = this.lmUrlUtilService.buildUrl({ resource: actionResource, params: { InboxNotificationDrillback: true } });
    this.lmLoggerService.log([ 'LMContextUtilService', 'processInboxNotification', 'Navigating to notification', actionResource ]);
    this.navigateWebAppUrl(url, { skipLocationChange: true });
  }

  private processInboxWorkflow(data: any): void {
    // Expect Drill back to contain:  dataArea={ID1}&workUnit={ID2}&activity={ID3}&queueAssignment={ID4}&csk.showusingxi=true&InboxWorkflowDrillback=true
    // ?dataArea=QAD221_AX2_HCM&workUnit=12&activity=1&queueAssignment=&csk.showusingxi=true&InboxWorkflowDrillback=true
    let drillback = data.applicationDrillback as string;
    if (!drillback || drillback.length === 0) {
      this.lmLoggerService.log([ 'LMContextUtilService', 'processInboxWorkflow received no drillback data.' ]);
      return;
    }
    drillback = decodeURIComponent(drillback);
    if (drillback.startsWith('?')) {
      drillback = drillback.substring(1);
    }
    const params = {} as { InboxWorkflowDrillback?: boolean, workUnit?: string, activity?: string, queueAssignment?: string, dataArea?: string };
    drillback.split('&').forEach(param => {
      const nameValue = param.split('=');
      params[nameValue[0]] = nameValue[1];
    });

    if (!params?.workUnit) {
      this.lmLoggerService.log([ 'LMContextUtilService', 'processInboxWorkflow', 'Workunit is not available in drillback data', data ]);
      return;
    }

    const url = window.location.protocol + '//' + window.location.host + '/bpm/ionInboxLoad?dataArea=' + params.dataArea
      + '&workUnit='+ params.workUnit + '&activity=' + params.activity + '&queueAssignment=' + params.queueAssignment
      + '&csk.showusingxi=true&output=JSON&showHtml=false';
    this.lmLoggerService.log([ 'LMContextUtilService', 'processInboxWorkflow', 'Navigating to IonInboxLoad to get redirectUrl for Workunit ', params.workUnit ] );
    this.lmBusyIndicatorService.showBusyIndicator();
    this.http.get(url).pipe(first()).subscribe((response: any) => {
      if (!response.redirectUrl) {
        this.lmLoggerService.log([ 'LMContextUtilService', 'processInboxWorkflow', 'Navigating to IonInboxLoad to get redirectUrl for Workunit ', params.workUnit, 'failed.' ] );
        this.lmBusyIndicatorService.hideBusyIndicator();
        this.lmErrorUtilService.handleError(response.errorMessage);
        return;
      }
      // Go to action redirect URL returned by BPM servlet. This will be a URL to form for process flow approval.
      // Remove &csk.hidetitlebar=true&csk.hidemenubar=true&csk.mi=true (These three are required in Mingle Inbox container)
      // Add fromInbox=true (It will paint the action buttons on the top and Inbox Notifications uses it).
      let redirectUrl = response.redirectUrl;
      redirectUrl = this.lmUrlUtilService.removeParams(redirectUrl, [ 'csk.mi', 'csk.hidetitlebar', 'csk.hidemenubar', 'showHtml', 'output' ]);
      redirectUrl = this.lmUrlUtilService.addParamToUrl(redirectUrl, 'fromInbox', 'true');
      this.navigateWebAppUrl(redirectUrl);
    },
    error => {
      this.lmBusyIndicatorService.hideBusyIndicator();
      // this.lmErrorUtilService.handleUnexpectedError(error);
      this.lmLoggerService.error(error);
      return throwError(error || 'Server error');
    });
  }

  private processLandmarkBIDrillback(data: any): void {
    // is url a BI Drillback - LandmarkBIDrillback URL param from Infor Application View > ViewId "LandmarkBIDrillback"
    const urlPdlOrDa = this.lmContextService.getUrlPdlOrDa();
    const url = window.location.protocol + '//' + window.location.host + '/' + urlPdlOrDa + '/web/bidrill' + data.applicationDrillback;

    // do not decode LandmarkBIDrillback messages, causes issues parsing the dimension data by BIDrillController
    this.lmGlobalUtilService.navigate(url);
  }

  private processSuiteHome(): void {
    const urlPdlOrDa = this.lmContextService.getUrlPdlOrDa();
    let url = window.location.protocol + '//' + window.location.host + '/' + urlPdlOrDa + '/web/home';
    url = this.lmUrlUtilService.updateContextSessionKeyParams(url);
    this.lmGlobalUtilService.navigate(url);
  }

  private processLinkDrillbackRequest(url: string) {
    this.lmUrlInfoBuilderService.processDrillbackAndCustomCodeUrl(url, 'process-link-drillback');

    // this.lmUrlUtilService.updateUrlParams(url, { output: 'spec' })
    //
    // if (url !== null) {
    //     url = url.replace('output=spec', '');
    //     this.navigateWebAppUrl(url);
    // }

    if (url === null || url.indexOf('/menu/') === 0) {
      this.navigateWebAppUrl(url);
      return;
    }


    this.lmSpecService.getApplicationMenuItemSpec(url).subscribe(response => {
      if (!response.dialogUrl) {
        this.navigateWebAppUrl(response.redirectUrl);
        return;
      }
      const actionName = this.lmUrlUtilService.getUrlParam(url, 'action');
      if (!actionName) {
        this.lmLoggerService.log('unable to find action param in url');
        return null;
      }

      this.lmSpecService.getFormSpecFromDialogActionUrl(response.dialogUrl).subscribe((formUrlResponse: LMFormSpecResponse) => {
        const formUrlInfo = formUrlResponse.formUrlInfo;
        const formSpec = formUrlResponse.formSpec;
        const actionSpec: LMActionSpec = formSpec.busClassSpec.actions.find(a => a.name === actionName);
        this.lmActionFormDialogService.runActionFormDialog(formUrlInfo, formSpec, actionSpec).subscribe();
      });
    });
  }

  private actionDrillbackRequest(url: string) {
    this.lmUrlInfoBuilderService.processDrillbackAndCustomCodeUrl(url, 'action-drillback');

    url = this.lmUrlUtilService.updateUrlParams(url, { output: 'spec' });
    url = this.lmUrlUtilService.updateContextSessionKeyParams(url);

    this.lmLoggerService.time({
      id: 'TotalActionForm',
      description: 'TOTAL ACTION FORM',
      className: 'LMContextUtilService',
      timingStart: 'LMContextUtilService -> actionDrillbackRequest()',
      timingEnd: 'LMFormContentComponent -> ngAfterViewInit()'
    });

    this.lmSpecService.getFormSpecFromRedirectActionUrl(url).subscribe((response: LMActionFormUrlResponse | LMSystemException) => {
      if ((response as LMSystemException).status
              && ((response as LMSystemException).status === AppConstants.STATUS_SYSTEM_ERROR || (response as LMSystemException).status === AppConstants.STATUS_UNAUTHORIZED)) {
        this.lmErrorUtilService.handleError((response as LMSystemException).statusText, (response as LMSystemException).details);
        return;
      }

      const formUrlInfo = (response as LMActionFormUrlResponse).formUrlInfo;
      const formSpec = (response as LMActionFormUrlResponse).formSpec;
      const resourceId = (response as LMActionFormUrlResponse).resourceId;
      const action = (response as LMActionFormUrlResponse).action;
      const actionSpec: LMActionSpec = formSpec.busClassSpec.actions.find(a => a.name === action);

      const urlCompiledTemplate = urltemplate.parse(formUrlInfo.initialDataUrl);
      const dataUrl = urlCompiledTemplate.expand({ resourceId, actionName: action });
      this.lmActionFormDialogService.getActionFormDialogDataView(dataUrl).subscribe((responseDV: any) => {
        const formDataView: LMDataView = responseDV;
        if (formDataView.status === AppConstants.STATUS_COMPLETED) {
          this.lmActionFormDialogService
            .runActionFormDialog(formUrlInfo, formSpec, actionSpec, formDataView, null, null, true)
            .subscribe((result: LMActionFormDialogResult) => {
              // Resend the last context message to restore the screen id
              if (this.lastBusinessContextMessage) {
                this.publishBusinessContext(this.lastBusinessContextMessage);
              }

              if (result.result === LMActionFormDialogResultCode.ACTION_FORM_DIALOG_SAVED) {
                const actionResult = result.transactionStateResult.getTransactionResult();
                if (actionResult.status === AppConstants.STATUS_COMPLETED) {
                  if (!actionResult.actionSpec.suppressCompletionMessage) {
                    this.lmGlobalUtilService.displayToastMessage(actionSpec.label, actionResult.message, AppConstants.TRA_ACTION_COMPLETED);
                  }
                  if (actionResult?.isCompletionNavigation && actionResult?.redirectUrl) {
                    // using injector due to circular dependency with LMNavigationService
                    this.injector.get(LMNavigationService).runNavigation(actionResult.redirectUrl);
                  }
                }
              }
            });
        } else {
          this.lmLoggerService.error(responseDV);
          this.lmErrorUtilService.handleError(responseDV.message, responseDV.details, responseDV.status, responseDV.statusText);
        }
      });
    });
  }

  private processStandardDrillbackRequest(url: string): void {
    url = this.lmUrlUtilService.toFullUrl(url); // is the drillback a relative url?
    url = this.lmUrlUtilService.remove3XParams(url);
    url = this.lmUrlInfoBuilderService.processDrillbackAndCustomCodeUrl(url, 'standard-drillback');
    url = this.lmUrlUtilService.updateContextSessionKeyParams(url);

    this.navigateWebAppUrl(url);
  }

  private navigateWebAppUrl(url: string, extras?: NavigationExtras) {
    // todo use lm-url-util.service standar url path split function
    //  to get the parts.
    //  for example: this.lmUrlUtilService.getWebAppNameFromUrl(url);
    let resourceType: string;
    let webAppName: string;
    const urlParts = url.split('/');
    if (urlParts.length >= 7) {
      webAppName = urlParts [ 4 ];
      resourceType = urlParts[ 5 ];
      const lastURLSection = urlParts[ 6 ].split('?');
      let resourceId = lastURLSection[ 0 ];
      resourceId = resourceId.replace('.', '_prd_');

      let settings: LMGlobalPersonalization = {};
      const bannerPersonalization: LMGlobalPersonalization = this.lmGlobalPersonalizationService.getGlobalPersonalization();
      if (!isEmpty(bannerPersonalization)) {
        settings = bannerPersonalization;
      }

      if (resourceType === 'menu') {
        settings.lastOpenedMenu = 'appmenu_' + resourceId;
      } else {
        if (!isEmpty(bannerPersonalization) && bannerPersonalization.lastOpenedMenu) {
          settings.lastOpenedMenu = '';
        }
      }
      this.lmGlobalPersonalizationService.saveGlobalPersonalization(settings);
    }

    if (resourceType && resourceType === 'land') {
      const webApp = (webAppName !== '') ? webAppName : this.lmContextService.getContext().webApp;
      url  =  '/' + this.lmContextService.getContext().urlPdlOrDa + '/' + webApp + '/home';
    }

    // todo for mingle drill backs this is already being called. consider remove one of them
    //  probably the one in the mingle drill back functions.
    url = this.lmUrlUtilService.updateContextSessionKeyParams(url);

    // LMCLIENT-35952 must put back in Angular Zone as Ming.le is not running in the zone.
    // Change Detection was not firing for certain drillback urls.
    this.ngZone.run(() =>
      this.lmGlobalUtilService.navigateByUrl(url, extras));
  }

  private publishBusinessContext(data: any) {
    // console.log('publishBusinessContext data=>');
    // console.log(data);
    this.lmLoggerService.log([ 'LMContextUtilService', 'publishBusinessContext', data ]);
    infor.companyon.client.sendMessage('inforBusinessContext', data);
  }

  private publishDrillbackMessage(data: any) {
    this.lmLoggerService.log([ 'LMContextUtilService', 'publishDrillbackMessage', data ]);
    infor.companyon.client.sendPrepareDrillbackMessage(data);
  }

  private publishShortcut(data: any) {
    // console.log('publishShortcut data=>');
    // console.log(data);
    this.lmLoggerService.log([ 'LMContextUtilService', 'publishShortcut', data ]);
    infor.companyon.client.sendMessage('setShortcutContext', data);
  }

  public publishStandardContext(messageId: string, data: any) {
    // console.log('publishStandardContext messageId = ' + messageId + ' data=>');
    // console.log(data);
    this.lmLoggerService.log([ 'LMContextUtilService', 'publishStandardContext', data ]);
    infor.companyon.client.sendMessage(messageId, data);
  }

  public setOverlayTitle(title: string) {
    this.lmLoggerService.log([ 'LMContextUtilService', 'setOverlayTitle', title ]);
    infor.companyon.client.sendMessage('overlay.title', title);
  }

  public sendOverlayCompleteMessage() {
    this.lmLoggerService.log([ 'LMContextUtilService', 'sendOverlayCompleteMessage' ]);
    infor.companyon.client.sendMessage('overlay.complete', {});
  }

  public sendToastMessage(title: string, message: string ) {
    this.lmLoggerService.log([ 'LMContextUtilService', 'sendToastMessage', title, message ]);
    const position = this.lmGlobalUtilService.getToastMessagePosition();
    infor.companyon.client.sendMessage('showToast', { title, message, position });
  }

  public showQRCodeScanner(): Observable<LMBarCodeData> {
    return new Observable<LMBarCodeData>(observer => {
      const data = { source: 'mobile', inputType: '1' };
      this.ngZone.runOutsideAngular(() => {
        infor.companyon.client.unRegisterMessageHandler('mobile_shareQRCodeData');
        infor.companyon.client.registerMessageHandler('mobile_shareQRCodeData', (data: LMBarCodeData) => {
          this.ngZone.run(() => {
            observer.next(data);
            observer.complete();
            infor.companyon.client.unRegisterMessageHandler('mobile_shareQRCodeData');
          });
        });
        infor.companyon.client.sendMessage('mobile_showQRCodeScanner', data);
      });
    });
  }
}
